import type { Unit } from '@lista/shared-types';

type T = (key: string, options?: Record<string, unknown>) => string;

const intlLocale = (lang: string) => (lang === 'ar' ? 'ar' : 'en');

/** "2 L", "1.5 kg", "3 pcs" - numbers localized, unit label from the shared catalog. */
export function formatQuantity(quantity: string, unit: Unit, t: T, lang: string): string {
  const n = Number(quantity);
  const num = Number.isFinite(n)
    ? new Intl.NumberFormat(intlLocale(lang), { maximumFractionDigits: 3 }).format(n)
    : quantity;
  return `${num} ${t(`units.short.${unit}`, { count: n })}`;
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 86_400],
  ['month', 30 * 86_400],
  ['week', 7 * 86_400],
  ['day', 86_400],
  ['hour', 3600],
  ['minute', 60],
];

/** "5 minutes ago", "yesterday", "now". */
export function formatRelative(iso: string, lang: string, now = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  // Hermes (React Native) may ship without Intl.RelativeTimeFormat.
  if (typeof Intl.RelativeTimeFormat !== 'function') return formatDate(iso, lang);
  const rtf = new Intl.RelativeTimeFormat(intlLocale(lang), { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(0, 'second');
}

export function formatDate(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(intlLocale(lang), { dateStyle: 'medium' }).format(new Date(iso));
}

export function formatNumber(n: number, lang: string): string {
  return new Intl.NumberFormat(intlLocale(lang)).format(n);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase() ||
    '?'
  );
}

/** Mirrors QuantitySchema: > 0, ≤ 9999, ≤ 3 decimals. */
export function isValidQuantity(value: string): boolean {
  if (!/^\d{1,4}(\.\d{1,3})?$/.test(value)) return false;
  const n = Number(value);
  return n > 0 && n <= 9999;
}

/** Step a quantity by ±1, clamped to 1…9999, keeping decimals exact. */
export function stepQuantity(value: string, delta: number): string {
  const n = Number(value);
  const base = Number.isFinite(n) ? n : 0;
  return String(Math.min(9999, Math.max(1, Math.round((base + delta) * 1000) / 1000)));
}

/** Category icons come from the database as names; both apps render them as emoji. */
const CATEGORY_EMOJI: Record<string, string> = {
  carrot: '🥕',
  milk: '🥛',
  drumstick: '🍗',
  fish: '🐟',
  croissant: '🥐',
  snowflake: '🧊',
  cup: '☕',
  cookie: '🍪',
  jar: '🫙',
  home: '🏠',
  'spray-can': '🧽',
  soap: '🧼',
  pill: '💊',
  baby: '🍼',
  paw: '🐾',
  pencil: '✏️',
  dots: '🛒',
};

export const categoryEmoji = (icon: string | null | undefined): string =>
  CATEGORY_EMOJI[icon ?? ''] ?? '🛒';
